import areEqual from "fbjs/lib/areEqual";
import * as React from "react";

import {
    InstanceToHeightMap,
    MultilineInstanceHeightInfo,
    MultilineRowHeightConsumer,
} from "../../Internals/ControlContexts/MultilineRowHeightContext";
import { PushInstance } from "../../Internals/ControlContexts/PathContexts/PushInstance";
import { nullElement } from "../../TypingUtils";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ProvideInstanceIndex } from "../../Internals/ControlContexts/PathContexts/InstanceIndexContext";

import { Cell, Line } from "./MultiLineSidebar.styles";

export interface MultiLineSidebarProps {
    bindingPath: AbsoluteResolvedModelPath;
    children: React.ReactNode;
    currentPage: number;
    displayedInstances: readonly string[];
    instancesPerPage: number;
    isList: boolean;
}

export class MultiLineSidebar extends React.Component<MultiLineSidebarProps> {
    public shouldComponentUpdate(nextProps: MultiLineSidebarProps): boolean {
        return (
            !this.props.bindingPath.isEquals(nextProps.bindingPath) ||
            this.props.children !== nextProps.children ||
            this.props.currentPage !== nextProps.currentPage ||
            !areEqual(this.props.displayedInstances, nextProps.displayedInstances) ||
            this.props.instancesPerPage !== nextProps.instancesPerPage ||
            this.props.isList !== nextProps.isList
        );
    }

    public render(): JSX.Element {
        const { bindingPath, currentPage, instancesPerPage, isList } = this.props;
        const startIndex = (currentPage - 1) * instancesPerPage;
        const instances = isList
            ? this.props.displayedInstances
            : this.props.displayedInstances.slice(startIndex, startIndex + instancesPerPage);

        return (
            <MultilineRowHeightConsumer bindingPath={bindingPath}>
                {({ rowHeight, lastRowHeight }: MultilineInstanceHeightInfo) => (
                    <div>
                        {this.renderRows(instances, rowHeight)}
                        {lastRowHeight ? <div style={{ height: lastRowHeight.maxHeight }} /> : nullElement}
                    </div>
                )}
            </MultilineRowHeightConsumer>
        );
    }

    private renderRows(instances: readonly string[], rowHeight: InstanceToHeightMap): JSX.Element[] {
        return instances.map((instanceId: string, instanceIndex: number) => (
            <ProvideInstanceIndex
                key={`${instanceId}_${instanceIndex}`}
                index={
                    this.props.isList
                        ? instanceIndex
                        : instanceIndex + (this.props.currentPage - 1) * this.props.instancesPerPage
                }
                bindingPath={this.props.bindingPath}>
                <PushInstance instance={instanceId} bindingPath={this.props.bindingPath}>
                    <Cell
                        data-tid="SidebarRow"
                        data-focus="noFocus"
                        style={{ height: rowHeight[instanceId]?.height ?? 0 }}>
                        <Line>{instanceId ? this.props.children : ""}</Line>
                    </Cell>
                </PushInstance>
            </ProvideInstanceIndex>
        ));
    }
}
